import { useCallback, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { OneDataCollection, F1SearchBox, Stack } from '@factorialco/f0-react/dist/experimental'
import { F0Box } from '@factorialco/f0-react'
import {
  RequestsForExternalUsersDocument,
  RequestsForExternalUsersQuery,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { encodeActivity } from 'modules/core/lib/activities/utils'
import i18n from 'modules/core/lib/i18n'

const APPROVALS_ID_PREFIX = 'Approvals::PendingApprovalsRequester'

type RequestNode = NonNullable<
  RequestsForExternalUsersQuery['notifications']
>['requestsConnection']['nodes'][number]

const RequestsForExternalUsers = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [search, setSearch] = useState('')

  const handleClickRequest = useCallback(
    (request: RequestNode) => {
      const isApproval = request.id.startsWith(APPROVALS_ID_PREFIX)

      if (isApproval && request.resourceIdentifier) {
        navigate({
          pathname,
          hash: encodeActivity('core.approvalRequest', {
            materializedApprovalFlowId: `${request.resourceIdentifier}`,
          }),
        })
        return
      }

      const url = request.actionUrl ?? request.remoteUrl
      if (!url) return

      if (url.startsWith('http://') || url.startsWith('https://')) {
        window.open(url, '_blank', 'noopener,noreferrer')
      } else {
        navigate(url)
      }
    },
    [navigate, pathname]
  )

  const searchLower = search.toLowerCase()

  const dataSource = useGraphqlDataCollectionSource<RequestNode>(
    {
      document: RequestsForExternalUsersDocument,
      paginationType: 'infinite-scroll',
      mapVariables: () => ({ first: 100 }),
      mapData: (data) => data.notifications?.requestsConnection?.nodes ?? [],
      mapPagination: (data) => ({
        total: data.notifications?.requestsConnection?.totalCount ?? 0,
        hasNextPage: false,
      }),
      itemPreFilter: (item) => {
        if (!searchLower) return true
        return (
          (item.text?.toLowerCase().includes(searchLower) ?? false) ||
          (item.categoryTitle?.toLowerCase().includes(searchLower) ?? false)
        )
      },
      itemOnClick: (item) => () => handleClickRequest(item),
      totalItemSummary: (totalItems) =>
        i18n.tp('inbox.requests_for_external_users.pending_requests', {
          count: totalItems,
        }),
    },
    [search, handleClickRequest]
  )

  const emptyStates = useMemo(
    () => ({
      'no-data': {
        emoji: '☕️',
        title: i18n.t('inbox.requests_for_external_users.empty'),
        description: i18n.t('inbox.requests_for_external_users.empty_description'),
      },
    }),
    []
  )

  return (
    <F0Box paddingX="lg" paddingTop="xl">
      <Stack alignItems="end">
        <F1SearchBox
          placeholder={i18n.t('inbox.requests_for_external_users.search_placeholder')}
          value={search}
          onChange={setSearch}
        />
      </Stack>
      <OneDataCollection
        id="inbox/requests/v1"
        source={dataSource}
        emptyStates={emptyStates}
        visualizations={[
          {
            type: 'list',
            options: {
              itemDefinition: (item) => {
                const request = item

                return {
                  title: request.text,
                  description: request.categoryTitle ? [request.categoryTitle] : undefined,
                }
              },
              fields: [
                {
                  label: i18n.t('common.due_date'),
                  render: (item) => {
                    const request = item

                    if (!request.dueDate) return

                    return {
                      type: 'tag' as const,
                      value: {
                        label: i18n.t('inbox.requests_for_external_users.due_date', {
                          date: request.dueDate,
                        }),
                      },
                    }
                  },
                },
              ],
            },
          },
        ]}
        fullHeight
      />
    </F0Box>
  )
}

export default RequestsForExternalUsers
